import React, { useState, useRef, useEffect, useCallback } from 'react'

const PAPER_DIMENSIONS = {
  a3: { portrait: { w: 297, h: 420 }, landscape: { w: 420, h: 297 } },
  a4: { portrait: { w: 210, h: 297 }, landscape: { w: 297, h: 210 } },
  a5: { portrait: { w: 148.5, h: 210 }, landscape: { w: 210, h: 148.5 } }
}

const MAX_WIDTH = 600
const RESIZE_HANDLE_SIZE = 12
const MIN_QR_SIZE_MM = 5

export default function QrPlacementPreview({
  assetUrl,
  assetFile,
  assetContentType,
  size,
  orientation,
  qrX,
  qrY,
  qrWidth,
  onChange
}) {
  const containerRef = useRef(null)
  const [dragging, setDragging] = useState(false)
  const [resizing, setResizing] = useState(false)
  const [dragStart, setDragStart] = useState(null)
  const [previewUrl, setPreviewUrl] = useState(null)

  // Generate preview URL for newly uploaded files
  useEffect(() => {
    if (!assetFile || !(assetFile instanceof File)) {
      setPreviewUrl(null)
      return
    }

    if (!assetFile.type.startsWith('image/')) {
      setPreviewUrl(null)
      return
    }

    const url = URL.createObjectURL(assetFile)
    setPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [assetFile])

  const paperDims = PAPER_DIMENSIONS[size]?.[orientation]

  const containerWidth = MAX_WIDTH
  const scale = paperDims ? containerWidth / paperDims.w : 1
  const containerHeight = paperDims ? paperDims.h * scale : 0

  const qrXPx = (qrX || 0) * scale
  const qrYPx = (qrY || 0) * scale
  const qrSizePx = (qrWidth || 40) * scale

  const isImageAsset = assetContentType && assetContentType.startsWith('image/')
  const imageUrl = previewUrl || (isImageAsset ? assetUrl : null)

  const clamp = useCallback((val, min, max) => Math.max(min, Math.min(max, val)), [])

  const handlePointerDown = useCallback((e, type) => {
    if (!paperDims) return
    e.preventDefault()
    e.stopPropagation()
    e.target.setPointerCapture(e.pointerId)
    setDragStart({ x: e.clientX, y: e.clientY, qrX: qrX || 0, qrY: qrY || 0, qrWidth: qrWidth || 40 })
    if (type === 'resize') {
      setResizing(true)
    } else {
      setDragging(true)
    }
  }, [qrX, qrY, qrWidth, paperDims])

  const handlePointerMove = useCallback((e) => {
    if (!dragStart || !paperDims) return

    const dx = e.clientX - dragStart.x
    const dy = e.clientY - dragStart.y

    if (resizing) {
      // Diagonal distance for square resize
      const delta = Math.max(dx, dy)
      const newWidthMm = clamp(
        dragStart.qrWidth + delta / scale,
        MIN_QR_SIZE_MM,
        Math.min(paperDims.w - dragStart.qrX, paperDims.h - dragStart.qrY)
      )
      onChange({
        qr_x: dragStart.qrX,
        qr_y: dragStart.qrY,
        qr_width: Math.round(newWidthMm * 10) / 10
      })
    } else if (dragging) {
      const currentWidth = dragStart.qrWidth
      const newX = clamp(dragStart.qrX + dx / scale, 0, paperDims.w - currentWidth)
      const newY = clamp(dragStart.qrY + dy / scale, 0, paperDims.h - currentWidth)
      onChange({
        qr_x: Math.round(newX * 10) / 10,
        qr_y: Math.round(newY * 10) / 10,
        qr_width: currentWidth
      })
    }
  }, [dragStart, dragging, resizing, scale, paperDims, clamp, onChange])

  const handlePointerUp = useCallback(() => {
    setDragging(false)
    setResizing(false)
    setDragStart(null)
  }, [])

  // Attach global pointer listeners during drag/resize
  useEffect(() => {
    if (!dragging && !resizing) return

    window.addEventListener('pointermove', handlePointerMove)
    window.addEventListener('pointerup', handlePointerUp)
    return () => {
      window.removeEventListener('pointermove', handlePointerMove)
      window.removeEventListener('pointerup', handlePointerUp)
    }
  }, [dragging, resizing, handlePointerMove, handlePointerUp])

  if (!paperDims) return null

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        QR Placement Preview
      </label>
      <div
        ref={containerRef}
        className="relative border-2 border-gray-300 rounded-lg overflow-hidden bg-gray-100 select-none touch-none"
        style={{ width: containerWidth, height: containerHeight }}
      >
        {imageUrl && (
          <img
            src={imageUrl}
            alt="Design preview"
            className="absolute inset-0 w-full h-full object-cover pointer-events-none"
            draggable={false}
          />
        )}

        {!imageUrl && (
          <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-sm">
            {assetContentType?.includes('pdf') ? 'PDF preview not available' : `${size?.toUpperCase()} ${orientation}`}
          </div>
        )}

        {/* QR overlay */}
        <div
          className="absolute border-2 border-dashed border-sky-500 bg-sky-500/20 flex items-center justify-center"
          style={{
            left: qrXPx,
            top: qrYPx,
            width: qrSizePx,
            height: qrSizePx,
            cursor: dragging ? 'grabbing' : 'grab'
          }}
          onPointerDown={(e) => handlePointerDown(e, 'drag')}
        >
          <span className="text-sky-700 text-xs font-medium pointer-events-none select-none">
            QR
          </span>

          {/* Resize handle */}
          <div
            className="absolute bottom-0 right-0 bg-sky-500 rounded-tl-sm"
            style={{
              width: RESIZE_HANDLE_SIZE,
              height: RESIZE_HANDLE_SIZE,
              cursor: 'nwse-resize'
            }}
            onPointerDown={(e) => handlePointerDown(e, 'resize')}
          />
        </div>
      </div>
      <p className="mt-1 text-xs text-gray-500">
        Drag to position, drag corner to resize. Paper: {paperDims.w} x {paperDims.h} mm
      </p>
    </div>
  )
}
